import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  fetchV2Detail,
  fetchV2Pdf,
  v2PdfUrl,
  type V2DetailPdf,
  type V2DetailResult,
} from "../api/v2";

// Each fetch re-signs the PDF URL, so do not refetch on focus/reconnect.
const options = {
  revalidateOnMount: true,
  dedupingInterval: 0,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

/** Props of AllMattersV2Panel that come from the detail and PDF requests. */
export interface V2DetailPanelData {
  data?: V2DetailResult;
  loading: boolean;
  error: boolean;
  pdf?: V2DetailPdf;
  pdfLoading: boolean;
  pdfError: boolean;
  onRetry: () => Promise<V2DetailResult | undefined>;
  onRetryPdf: () => Promise<V2DetailPdf | undefined>;
}

/**
 * Fetches a detail's result and its PDF in parallel. The result can take
 * seconds (S3 and number annotations); the PDF must not wait for it.
 */
export const useV2Detail = (
  detailUrl: string | null
): { panel: V2DetailPanelData; clear: () => void } => {
  const { apiClient } = useApiClient();
  const detail = useSWR<V2DetailResult, Error>(
    detailUrl,
    async (url: string) => await fetchV2Detail(apiClient, url),
    options
  );
  const pdf = useSWR<V2DetailPdf, Error>(
    detailUrl ? v2PdfUrl(detailUrl) : null,
    async (url: string) => await fetchV2Pdf(apiClient, url),
    options
  );
  return {
    panel: {
      data: detail.data,
      loading: detail.isLoading,
      error: Boolean(detail.error),
      pdf: pdf.data,
      pdfLoading: pdf.isLoading,
      pdfError: Boolean(pdf.error),
      onRetry: async () => (await detail.mutate()) ?? undefined,
      onRetryPdf: async () => (await pdf.mutate()) ?? undefined,
    },
    clear: () => {
      void detail.mutate(undefined, { revalidate: false });
      void pdf.mutate(undefined, { revalidate: false });
    },
  };
};
